/**
 * Render "text with $LaTeX$ islands" to HTML.
 *
 * Every subject agent (math, physics, chemistry…) writes formulas as
 * LaTeX inside `$…$` / `$$…$$` — that is how the chat shows them, via
 * remark-math + rehype-katex. Places that print model output *outside*
 * Markdown (the homework sheet, its in-app preview) used to escape the
 * text as-is, so teachers saw "$(-3) + (-7)$" literally (2026-09-14).
 *
 * Output is MathML only (`output: "mathml"`): it needs no stylesheet and
 * no web fonts, so the self-contained printable HTML stays self-contained
 * and the sandboxed preview iframe (opaque origin, can't load our fonts)
 * renders it too. Every current browser has native MathML.
 *
 * Non-math text is HTML-escaped; newlines become <br> when asked.
 */
import katex from "katex";

// $$…$$ first (display), then $…$ (inline). Inline candidates are any
// `$…$` pair on one line; `isMath` then decides. Strict remark-math rules
// (no whitespace right after the opening `$` or before the closing one,
// closing `$` not followed by a digit) accept the candidate outright.
// Padded ones — the model writes "$|{-15}| - |{-6}| = $ ______" — are
// accepted only when the content looks like TeX (an operator, a backslash
// command, braces…) and carries no CJK, so "$5 和 $7" / "$5 and $7"
// (prices) still print literally.
const MATH_SRC = String.raw`\$\$([\s\S]+?)\$\$|\$((?:\\\$|[^$\n])+?)\$(?!\d)`;
const TEXY = /[\\{}^_=<>|+*/]|\d\s*[-−]\s*\d/;
const CJK = /[\u3400-\u9fff]/;

function isInlineMath(content: string): boolean {
  if (!content.trim()) return false;
  if (!/^\s/.test(content) && !/\s$/.test(content)) return true;
  return TEXY.test(content) && !CJK.test(content);
}

function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function tex(src: string, display: boolean): string {
  try {
    return katex.renderToString(src, {
      output: "mathml",
      displayMode: display,
      throwOnError: false,
      strict: "ignore",
    });
  } catch {
    return esc(display ? `$$${src}$$` : `$${src}$`);
  }
}

export function mathToHtml(text: string, opts: { breaks?: boolean } = {}): string {
  const s = String(text ?? "");
  let out = "";
  let last = 0;
  // Fresh regex per call: a shared global regex carries lastIndex across
  // calls, silently skipping early matches. Manual exec loop so a rejected
  // candidate can rewind to just past its opening `$`.
  const re = new RegExp(MATH_SRC, "g");
  let m: RegExpExecArray | null;
  while ((m = re.exec(s)) !== null) {
    const idx = m.index;
    out += esc(s.slice(last, idx));
    if (m[1] === undefined && !isInlineMath(m[2] ?? "")) {
      // Not math: emit the opening `$` literally and rescan from after it.
      out += "$";
      last = idx + 1;
      re.lastIndex = last;
      continue;
    }
    out += m[1] !== undefined ? tex(m[1], true) : tex((m[2] ?? "").trim().replace(/\\\$/g, "$"), false);
    last = idx + m[0].length;
  }
  out += esc(s.slice(last));
  // Safety net for the one Markdown habit the model keeps despite the
  // prompt: **bold**. Everything else prints verbatim by design.
  out = out.replace(/\*\*([^*\n]+?)\*\*/g, "<b>$1</b>");
  return opts.breaks ? out.replace(/\n/g, "<br>") : out;
}

/** True when the text contains at least one renderable math island. */
export function hasMath(text: string): boolean {
  const re = new RegExp(MATH_SRC, "g");
  let m: RegExpExecArray | null;
  while ((m = re.exec(String(text ?? ""))) !== null) {
    if (m[1] !== undefined || isInlineMath(m[2] ?? "")) return true;
    re.lastIndex = m.index + 1;
  }
  return false;
}
